import { createContext, useState, useContext, useEffect } from 'react'
import type { ReactNode } from 'react'
import { api } from '../lib/api'
import { toast } from 'react-toastify'
import { identifyUser, resetUser, trackEvent, ANALYTICS_EVENTS } from '../lib/analytics'

interface Brand {
  userBrandId: string  // UserBrand ID (for user-specific operations)
  brandId: string      // Brand ID (for shared brand data)
  id: string           // Alias for brandId (for backward compatibility)
  companyName: string
  name?: string        // Alias for companyName
  domain: string       // Changed from website
  website?: string     // Deprecated, use domain
  industry?: string
  description?: string
  aiIndexScore: number
  subscriptionPlan?: string
  hasEvaluate?: boolean
  hasMonitor?: boolean
  isOwner: boolean     // Can user edit this brand?
  scoreBreakdown?: any // Score breakdown from AI Brand Index
  hasLlmsTxt?: boolean
  hasManifest?: boolean
  contentAccessible?: boolean
  contentBlockType?: string | null
  contentAccessibilityNotes?: string | null

  // Brand-level subscription fields
  subscriptionProduct?: 'STARTER' | 'GROWTH' | 'PRO' | null
  subscriptionStatus?: 'ACTIVE' | 'TRIAL' | 'PAST_DUE' | 'CANCELED' | 'UNPAID'
  stripeSubscriptionId?: string | null
  stripePriceId?: string | null
  currentPeriodStart?: string | null
  currentPeriodEnd?: string | null
  cancelAtPeriodEnd?: boolean
  canceledAt?: string | null
  trialEndsAt?: string | null
  analysisFrequency?: 'MONTHLY' | 'WEEKLY'
  lastAnalyzedAt?: string | null
  nextScheduledAnalysis?: string | null
}

interface User {
  id: string
  email: string
  firstName?: string
  lastName?: string
  isAdmin?: boolean
  userBrands?: Brand[] // Changed from brands
  brands?: Brand[]     // Deprecated alias for userBrands
  activeUserBrandId?: string  // Changed from activeBrandId
  activeBrandId?: string      // Deprecated, use activeUserBrandId
  activeBrand?: Brand

  // Subscription fields
  subscriptionTier: 'FREE' | 'BRAND' | 'CORPORATION' | 'AGENCY' | 'AGENCY_XL'
  subscriptionQuantity?: number
  subscriptionStatus: 'ACTIVE' | 'TRIAL' | 'PAST_DUE' | 'CANCELED' | 'UNPAID'
  trialEndsAt?: string
  accountTier?: string  // Alias for subscriptionTier (for backward compatibility)
  subscriptionEndsAt?: string
  cancelAtPeriodEnd?: boolean
}

interface ImpersonationInfo {
  isImpersonating: boolean
  admin: {
    id: string
    email?: string
    firstName?: string
    lastName?: string
  }
  reason?: string
}

interface AuthContextType {
  currentUser: User | null
  activeBrand: Brand | null
  brands: Brand[]
  impersonation: ImpersonationInfo | null
  login: (email: string, password: string) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  logout: () => void
  loading: boolean
  switchBrand: (brandId: string) => Promise<void>
  createBrand: (data: BrandData) => Promise<void>
  refreshUser: () => Promise<void>
  stopImpersonation: () => Promise<void>
}

interface RegisterData {
  email: string
  password: string
  firstName?: string
  lastName?: string
  companyName?: string
  website?: string
  industry?: string
  description?: string
  skipBrand?: boolean
}

interface BrandData {
  companyName: string
  website?: string
  industry?: string
  description?: string
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [activeBrand, setActiveBrand] = useState<Brand | null>(null)
  const [brands, setBrands] = useState<Brand[]>([])
  const [impersonation, setImpersonation] = useState<ImpersonationInfo | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Try to fetch current user - cookies will be sent automatically
    fetchCurrentUser()
  }, [])

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me')
      const userData = response.data
      setCurrentUser(userData)
      // Support both old (brands) and new (userBrands) structures
      setBrands(userData.userBrands || userData.brands || [])
      setActiveBrand(userData.activeBrand || null)
      // Store impersonation info if present
      setImpersonation(userData.impersonation || null)

      // Identify user in PostHog for analytics
      identifyUser(userData.id, {
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        subscriptionTier: userData.subscriptionTier,
        subscriptionStatus: userData.subscriptionStatus,
        brandCount: (userData.userBrands || userData.brands || []).length
      })
    } catch (error) {
      // Cookie auth - no need to remove localStorage token
      // If 401/403, user simply doesn't have valid session
      setCurrentUser(null)
      setActiveBrand(null)
      setBrands([])
      setImpersonation(null)
    } finally {
      setLoading(false)
    }
  }

  const login = async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password })
    const { user } = response.data
    // Cookie is set automatically by the server
    setCurrentUser(user)
    // Support both old (brands) and new (userBrands) structures
    setBrands(user.userBrands || user.brands || [])
    setActiveBrand(user.activeBrand || null)

    // Track login event and identify user
    identifyUser(user.id, {
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      subscriptionTier: user.subscriptionTier,
      subscriptionStatus: user.subscriptionStatus,
      brandCount: (user.userBrands || user.brands || []).length
    })
    trackEvent(ANALYTICS_EVENTS.USER_LOGGED_IN, {
      subscriptionTier: user.subscriptionTier,
      brandCount: (user.userBrands || user.brands || []).length
    })
  }

  const register = async (data: RegisterData) => {
    const response = await api.post('/auth/register', data)
    const { user } = response.data
    // Cookie is set automatically by the server
    setCurrentUser(user)
    // Support both old (brands) and new (userBrands) structures
    setBrands(user.userBrands || (user.activeBrand ? [user.activeBrand] : []))
    setActiveBrand(user.activeBrand || null)

    // Track signup event and identify user
    identifyUser(user.id, {
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      subscriptionTier: user.subscriptionTier,
      subscriptionStatus: user.subscriptionStatus
    })
    trackEvent(ANALYTICS_EVENTS.USER_SIGNED_UP, {
      industry: data.industry
    })
  }

  const switchBrand = async (brandId: string) => {
    try {
      await api.post(`/brands/${brandId}/activate`)
      // Update local state
      const brand = brands.find(b => b.id === brandId)
      if (brand) {
        setActiveBrand(brand)
        setCurrentUser(prev => prev ? { ...prev, activeBrandId: brandId, activeBrand: brand } : null)
        toast.success(`Switched to ${brand.companyName}`)
      }
      // Refresh user data to ensure consistency
      await fetchCurrentUser()
    } catch (error) {
      toast.error('Failed to switch brand')
      throw error
    }
  }

  const createBrand = async (data: BrandData) => {
    try {
      const response = await api.post('/brands', data)
      const newBrand = response.data

      // Update local state
      setBrands(prev => [...prev, newBrand])

      // If this is the first brand, set it as active
      if (brands.length === 0) {
        setActiveBrand(newBrand)
        setCurrentUser(prev => prev ? { ...prev, activeBrandId: newBrand.id, activeBrand: newBrand } : null)
      }

      // Track brand creation
      trackEvent(ANALYTICS_EVENTS.BRAND_CREATED, {
        industry: data.industry,
        brandCount: brands.length + 1
      })

      toast.success('Brand created successfully')

      // Refresh user data to ensure consistency
      await fetchCurrentUser()

      return newBrand
    } catch (error) {
      toast.error('Failed to create brand')
      throw error
    }
  }

  const refreshUser = async () => {
    await fetchCurrentUser()
  }

  const stopImpersonation = async () => {
    try {
      const response = await api.post('/admin/stop-impersonation')
      if (response.data.success) {
        toast.success('Impersonation ended')
        // If session was restored, refresh user data to get admin's data
        if (response.data.data?.sessionRestored) {
          await fetchCurrentUser()
        } else {
          // No saved admin session - redirect to login
          setCurrentUser(null)
          setActiveBrand(null)
          setBrands([])
          setImpersonation(null)
          window.location.href = '/login'
        }
      }
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to stop impersonation'
      toast.error(message)
      console.error('Failed to stop impersonation:', error)
    }
  }

  const logout = async () => {
    try {
      // Track logout event before resetting identity
      trackEvent(ANALYTICS_EVENTS.USER_LOGGED_OUT)
      // Call API to invalidate session
      await api.post('/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
      // Continue with local logout even if API fails
    } finally {
      // Reset PostHog user identity
      resetUser()
      // Clear local state
      setCurrentUser(null)
      setActiveBrand(null)
      setBrands([])
      // Cookie is cleared by the server
    }
  }

  const value = {
    currentUser,
    activeBrand,
    brands,
    impersonation,
    login,
    register,
    logout,
    loading,
    switchBrand,
    createBrand,
    refreshUser,
    stopImpersonation
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}